'use client';

import { useState } from 'react';
import { Check, Copy, Code2, ArrowUp, MessageCircle, Award, LayoutList } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { appConfig } from '@/lib/config';

interface ProductEmbedWidgetProps {
  slug: string;
  productName: string;
}

const THEMES = [
  { id: 'card', label: 'Карточка', icon: LayoutList, width: 320, height: 100, query: '' },
  { id: 'vote', label: 'Голосование', icon: ArrowUp, width: 360, height: 100, query: '?theme=vote' },
  { id: 'comments', label: 'Комментарии', icon: MessageCircle, width: 360, height: 100, query: '?theme=comments' },
  { id: 'badge', label: 'Бейдж', icon: Award, width: 250, height: 44, query: '?theme=badge' },
] as const;

function buildQuery(theme: typeof THEMES[number], dark: boolean, outline: boolean): string {
  const parts: string[] = [];
  if (theme.query) parts.push(theme.query.slice(1));
  if (dark) parts.push('dark=1');
  if (outline) parts.push('outline=1');
  return parts.length ? '?' + parts.join('&') : '';
}

export function ProductEmbedWidget({ slug, productName }: ProductEmbedWidgetProps) {
  const [activeTheme, setActiveTheme] = useState('card');
  const [darkMode, setDarkMode] = useState(false);
  const [outline, setOutline] = useState(false);
  const [copied, setCopied] = useState(false);

  const theme = THEMES.find((t) => t.id === activeTheme)!;
  const isBadge = activeTheme === 'badge';
  const query = buildQuery(theme, darkMode, outline);
  const embedUrl = `${appConfig.url}/embed/${slug}${query}`;
  const radius = isBadge ? '99px' : '16px';
  const embedCode = `<iframe src="${embedUrl}" width="${theme.width}" height="${theme.height}" frameborder="0" style="border:none;border-radius:${radius};overflow:hidden;"></iframe>`;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(embedCode);
      setCopied(true);
      toast.success('Код скопирован');
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Не удалось скопировать');
    }
  };

  return (
    <div className="bg-card border border-border rounded-2xl overflow-hidden">
      {/* Header */}
      <div className="px-5 py-4 sm:px-6 border-b border-border flex items-center gap-3">
        <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-400/10">
          <Code2 className="h-4 w-4 text-foreground" />
        </div>
        <h3 className="text-sm font-semibold text-foreground">Встроить на сайт</h3>
      </div>

      <div className="px-5 py-5 sm:px-6 sm:py-6 space-y-5">
        {/* Theme selector */}
        <div>
          <div className="text-xs font-medium text-foreground-tertiary mb-2">Вид виджета</div>
          <div className="flex flex-wrap gap-2">
            {THEMES.map((t) => {
              const Icon = t.icon;
              const isActive = activeTheme === t.id;
              return (
                <button
                  key={t.id}
                  onClick={() => setActiveTheme(t.id)}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors',
                    isActive
                      ? 'bg-foreground text-background'
                      : 'bg-muted text-foreground-secondary hover:text-foreground'
                  )}
                >
                  <Icon className="h-4 w-4" />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Style options */}
        <div>
          <div className="text-xs font-medium text-foreground-tertiary mb-2">Оформление</div>
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setDarkMode(false)}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors',
                !darkMode
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-foreground-secondary hover:text-foreground'
              )}
            >
              Светлое
            </button>
            <button
              onClick={() => setDarkMode(true)}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors',
                darkMode
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-foreground-secondary hover:text-foreground'
              )}
            >
              Тёмное
            </button>

            <div className="w-px bg-border mx-1 self-stretch" />

            <button
              onClick={() => setOutline(false)}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors',
                !outline
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-foreground-secondary hover:text-foreground'
              )}
            >
              Без рамки
            </button>
            <button
              onClick={() => setOutline(true)}
              className={cn(
                'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium transition-colors',
                outline
                  ? 'bg-foreground text-background'
                  : 'bg-muted text-foreground-secondary hover:text-foreground'
              )}
            >
              С рамкой
            </button>
          </div>
        </div>

        {/* Preview */}
        <div
          className={cn('flex items-center justify-center', isBadge ? 'py-4 px-4' : 'py-5 px-4')}
          style={{ background: darkMode ? '#1c1c1e' : '#f0f0f2' }}
        >
          <iframe
            key={`${activeTheme}-${darkMode}-${outline}`}
            src={embedUrl}
            width={theme.width}
            height={theme.height}
            style={{ border: 'none', display: 'block', maxWidth: '100%', colorScheme: 'normal' }}
            title={`${productName} embed`}
          />
        </div>

        {/* Code */}
        <div className="relative">
          <pre className="bg-muted/60 border border-border rounded-xl px-4 py-3 pr-28 text-[11px] text-foreground-secondary font-mono overflow-x-auto whitespace-pre-wrap break-all leading-relaxed">
            {embedCode}
          </pre>
          <Button
            size="sm"
            variant="outline"
            onClick={handleCopy}
            className={cn(
              'absolute top-2 right-2 h-7 gap-1.5 text-[11px] transition-all',
              copied && 'bg-emerald-500/10 border-emerald-500/30 text-emerald-600'
            )}
          >
            {copied ? (
              <><Check className="h-3 w-3" />Скопировано</>
            ) : (
              <><Copy className="h-3 w-3" />Копировать</>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
